import React, { useState, useEffect } from 'react';
import './Deposit.css';
import { authService } from '../services/authService';

const Deposit = () => {
  const [mode, setMode] = useState('bank');
  const [amount, setAmount] = useState('');
  const [utr, setUtr] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // NEW STATE FOR ADMIN PAYMENT DETAILS
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [loadingPayment, setLoadingPayment] = useState(true);

  // FETCH ADMIN QR / PAYMENT DETAILS
  useEffect(() => {
    const fetchPaymentDetails = async () => {
      try {
        const token = authService.getToken();

const res = await fetch(
  `https://playinbyd.com/api/admin/qrcode-images-list-grid-data?accessing=8-3&unique_jwt_token=${token}`
);

        const data = await res.json();

        if (data.success) {
          setPaymentDetails(data.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch payment details', err);
      } finally {
        setLoadingPayment(false);
      }
    };

    fetchPaymentDetails();
  }, []);

  const validateAmount = (value) => {
    if (!value) return 'Amount is required';
    const num = parseFloat(value);
    if (isNaN(num)) return 'Please enter a valid number';
    if (num <= 0) return 'Amount must be greater than 0';
    if (num > 1000000) return 'Amount cannot exceed ₹10,00,000';
    if (!/^\d+(\.\d{0,2})?$/.test(value)) return 'Maximum 2 decimal places allowed';
    return '';
  };

  const validateUtr = (value) => {
    if (!value.trim()) return 'UTR is required';
    if (value.length > 30) return 'UTR cannot exceed 30 characters';
    if (!/^[A-Za-z0-9]+$/.test(value)) return 'UTR should contain only letters and numbers';
    return '';
  };

  const validate = () => {
    const err = {};
    const amountError = validateAmount(amount);
    const utrError = validateUtr(utr);

    if (amountError) err.amount = amountError;
    if (utrError) err.utr = utrError;

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;

    if (value === '') {
      setAmount('');
      if (errors.amount) {
        setErrors(prev => ({ ...prev, amount: '' }));
      }
      return;
    }

    const num = parseFloat(value);
    if (isNaN(num)) return;

    const parts = value.split('.');
    if (parts[0].length > 8) return;

    if (parts.length > 1 && parts[1].length > 2) return;

    if (num > 1000000) return;

    if (num < 0) return;

    setAmount(value);
    if (errors.amount) {
      setErrors(prev => ({ ...prev, amount: '' }));
    }
  };

  const handleUtrChange = (e) => {
    const value = e.target.value;

    if (value.length > 30) return;

    if (!/^[A-Za-z0-9]*$/.test(value)) return;

    setUtr(value.toUpperCase());
    if (errors.utr) {
      setErrors(prev => ({ ...prev, utr: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const token = authService.getToken();
    if (!token) {
      setMessage('Please login again');
      return;
    }

    const payload = {
      app_key: 'SgRTXsywVmWteEBLlYWecwgbDiHwlh',
      env_type: 'Prod',
      unique_jwt_token: token,
      amount: Number(amount),
      txn_ref: utr,
      upigpay: mode === 'wallet' ? 1 : 0,
      upiphonepe: 0,
      otherupi: mode === 'bank' ? 'Add Fund' : ''
    };

    try {
      setLoading(true);
      setMessage('');

      const res = await fetch(
        'https://playinbyd.com/api/api-add-money-via-upi',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );

      const data = await res.json();

      if (data.success) {
        setMessage('Deposit request submitted successfully');
        setAmount('');
        setUtr('');
        setErrors({});
      } else {
        setMessage(data.msg || 'Deposit failed');
      }
    } catch {
      setMessage('Server error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bet-history-page">
      <div className="deposit-page">
        <div className="main-card">

          {/* HEADER */}
          <div className="main-card-header">
            <h1 className="page-title">Deposit</h1>
          </div>

          {/* MODE SWITCH */}
          <div className="session-toggle">
            <button
              type="button"
              className={`session-btn ${mode === 'bank' ? 'active' : ''}`}
              onClick={() => setMode('bank')}
            >
              🏦 Bank
            </button>

            <button
              type="button"
              className={`session-btn ${mode === 'wallet' ? 'active' : ''}`}
              onClick={() => setMode('wallet')}
            >
              💰 Wallet
            </button>
          </div>

          {/* ADMIN PAYMENT DETAILS */}
          {loadingPayment ? (
            <div className="payment-loading">Loading payment details...</div>
          ) : (
            paymentDetails.map((item) => (
              <div className="payment-box" key={item._id}>

                <h3 className="payment-title">{item.qr_title}</h3>

                <img
                  src={item.qr_image}
                  alt="QR Code"
                  className="qr-image"
                />

                <div className="payment-info">
                  <p><strong>UPI ID:</strong> {item.upi_id}</p>
                  {item.description && (
                    <p><strong>Note:</strong> {item.description}</p>
                  )}
                </div>

              </div>
            ))
          )}

          {/* FORM */}
          <form className="deposit-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Amount *</label>
              <input
                className="no-scroll"
                type="number"
                placeholder="Enter amount (max ₹10,00,000)"
                value={amount}
                onChange={handleAmountChange}
                onBlur={() => {
                  if (amount) {
                    const error = validateAmount(amount);
                    if (error) {
                      setErrors(prev => ({ ...prev, amount: error }));
                    }
                  }
                }}
                min="0"
                step="0.01"
                inputMode="decimal"
                max="1000000"
              />
              {errors.amount && <div className="error">{errors.amount}</div>}
              <div className="hint">Maximum 8 digits and 2 decimal places</div>
            </div>

            <div className="form-group">
              <label>UTR *</label>
              <input
                type="text"
                placeholder="Enter UTR (max 30 alphanumeric characters)"
                value={utr}
                onChange={handleUtrChange}
                onBlur={() => {
                  if (utr) {
                    const error = validateUtr(utr);
                    if (error) {
                      setErrors(prev => ({ ...prev, utr: error }));
                    }
                  }
                }}
                maxLength="30"
                pattern="[A-Za-z0-9]+"
                title="Only letters and numbers allowed"
              />
              {errors.utr && <div className="error">{errors.utr}</div>}
              <div className="hint">Only letters and numbers, max 30 characters</div>
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit Deposit'}
            </button>

            {message && (
              <div className={message.includes('success') ? 'success' : 'error'}>
                {message}
              </div>
            )}
          </form>

        </div>
      </div>
    </div>
  );
};

export default Deposit;